// ----------------------------------------------------------------------------
// HTTP client for the Laravel backend
// ----------------------------------------------------------------------------
// Toggle live mode by setting the following two env vars in /app/frontend/.env:
//   REACT_APP_BACKEND_URL=https://api.victoriaresin.store
//   REACT_APP_USE_LIVE_API=true
//
// When USE_LIVE_API is not "true" the services fall back to the local mock
// implementation so the preview and offline dev continue to work.
// ----------------------------------------------------------------------------

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
export const API_BASE = BACKEND ? `${BACKEND}/api` : '/api';
export const USE_LIVE_API = process.env.REACT_APP_USE_LIVE_API === 'true';

const AUTH_KEY = 'pra_auth_v2';   // same key used by AuthContext

function readToken() {
  try {
    const raw = sessionStorage.getItem(AUTH_KEY) || localStorage.getItem(AUTH_KEY);
    return raw ? (JSON.parse(raw).token || null) : null;
  } catch { return null; }
}

async function parse(res) {
  const text = await res.text();
  let json = {};
  
  // ✅ FIX 1: Prevent crashing on empty JSON (e.g. Laravel 204 No Content)
  try {
    json = text ? JSON.parse(text) : {};
  } catch (e) {
    // If it's not JSON, use the text as the message
    json = { message: text || 'Unknown error' };
  }

  if (!res.ok) {
    const err = new Error(json.message || res.statusText || 'Request failed');
    err.status = res.status;
    err.data = json;
    throw err;
  }
  return json;
}

async function request(method, path, body, opts = {}) {
  const token = readToken();
  const headers = { Accept: 'application/json' };

  // Handle body
  if (body instanceof FormData) {
    // Browser sets Content-Type with boundary automatically, so skip setting it
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (token && !opts.skipAuth) {
    headers.Authorization = `Bearer ${token}`;
  }

  // ✅ FIX 2: Merge custom headers and fetch options properly
  if (opts.headers) {
    Object.assign(headers, opts.headers);
  }

  // Extract skipAuth so it doesn't get passed to the native fetch
  const { skipAuth, ...fetchOpts } = opts;

  const init = {
    method,
    headers,
    ...fetchOpts, // Now you can pass cache, signal, etc. to fetch
  };

  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  return parse(await fetch(`${API_BASE}${path}`, init));
}

export const http = {
  get:  (p, opts)     => request('GET',  p, undefined, opts),
  post: (p, b, opts)  => request('POST', p, b, opts),
  put:  (p, b, opts)  => request('PUT',  p, b, opts),
  patch:(p, b, opts)  => request('PATCH',p, b, opts),
  del:  (p, opts)     => request('DELETE', p, undefined, opts),
};

/**
 * Run a live-API call, and if `USE_LIVE_API` is off (or the call fails with
 * a network error / 5xx), fall back to the provided mock function.
 * Any 4xx from live still bubbles up so form validation errors surface.
 */
export async function callOrMock(liveFn, mockFn) {
  if (!USE_LIVE_API) return mockFn();
  try {
    return await liveFn();
  } catch (e) {
    if (e && e.status && e.status >= 400 && e.status < 500) throw e;
    console.warn('[api] live call failed, falling back to mock:', e?.message);
    return mockFn();
  }
}